import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  CalendarClock,
  CalendarDays,
  CircleDot,
  Clock3,
  Copy,
  ImageOff,
  Mail,
  Megaphone,
  MessageSquare,
  Monitor,
  MoreVertical,
  Pause,
  Pencil,
  Play,
  Plus,
  Search,
  Siren,
  Tag,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { useAssetUrls } from '../components/EntityForm';
import { Empty, ErrorBox, Field, Loading, PageHeader, timeAgo } from '../components/ui';
import { SendMessageModal } from './GuestsPage';
import { Select } from '../components/Select';
import { Pager, usePaged } from '../components/Pager';
import { ask } from '../components/Confirm';
import { Menu } from '../components/Menu';

export interface Audience {
  roomIds?: string[];
  zoneIds?: string[];
  roomKinds?: string[];
  packageIds?: string[];
  languages?: string[];
  vip?: boolean;
  occupiedOnly?: boolean;
}

export interface Message {
  id: string;
  type: 'EMERGENCY' | 'OPERATIONAL' | 'GUEST' | 'PMS' | 'MARKETING' | 'PROMO';
  format: 'TOAST' | 'BANNER' | 'POPUP' | 'FULLSCREEN' | 'TICKER' | 'CARD';
  name: string | null;
  title: string | null;
  body: string | null;
  imageAssetId: string | null;
  ctaLabel: string | null;
  qrUrl: string | null;
  audience: Audience;
  trigger: 'IMMEDIATE' | 'SCHEDULED' | 'CHECK_IN' | 'BEFORE_CHECKOUT';
  triggerHours: number | null;
  startAt: string | null;
  endAt: string | null;
  dailyFrom: string | null;
  dailyTo: string | null;
  frequency: 'ALWAYS' | 'ONCE' | 'ONCE_PER_STAY' | 'ONCE_PER_DAY' | 'EVERY_HOURS';
  everyHours: number | null;
  maxImpressions: number | null;
  stopOnDismiss: boolean;
  durationSec: number | null;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ENDED';
  createdAt: string;
  updatedAt: string;
}

export const FORMAT_LABEL: Record<Message['format'], string> = {
  POPUP: 'Pop-up',
  FULLSCREEN: 'Full screen',
  BANNER: 'Banner',
  TOAST: 'Toast',
  TICKER: 'Ticker',
  CARD: 'Home card',
};
export const TRIGGER_LABEL: Record<Message['trigger'], string> = {
  IMMEDIATE: 'As soon as possible',
  SCHEDULED: 'On a schedule',
  CHECK_IN: 'Welcome (after check-in)',
  BEFORE_CHECKOUT: 'Before check-out',
};
export const FREQ_LABEL: Record<Message['frequency'], string> = {
  ONCE: 'Once per TV',
  ONCE_PER_STAY: 'Once per stay',
  ONCE_PER_DAY: 'Once a day',
  EVERY_HOURS: 'Every N hours',
  ALWAYS: 'Always (every time conditions match)',
};
export const TYPE_LABEL: Record<Message['type'], string> = {
  MARKETING: 'Marketing',
  PROMO: 'Background promo',
  OPERATIONAL: 'Operational',
  GUEST: 'Guest',
  PMS: 'PMS',
  EMERGENCY: 'Emergency',
};
export const TYPE_TINT: Record<Message['type'], [string, string]> = {
  MARKETING: ['#6d28d9', '#f1ebff'],
  PROMO: ['#be185d', '#fde8f1'],
  OPERATIONAL: ['#b45309', '#fff4e0'],
  GUEST: ['#0f766e', '#e0f5f2'],
  PMS: ['#1d4ed8', '#e8f0ff'],
  EMERGENCY: ['#b91c1c', '#fdecec'],
};
export const ROOM_KINDS = ['GUEST_ROOM', 'SUITE', 'LOBBY', 'PUBLIC', 'STAFF', 'OTHER'];

const isScheduled = (m: Message) => m.status === 'ACTIVE' && !!m.startAt && new Date(m.startAt) > new Date();
export const STATE: Record<Message['status'] | 'SCHEDULED', { label: string; cls: string }> = {
  ACTIVE: { label: 'Active', cls: 'online' },
  SCHEDULED: { label: 'Scheduled', cls: 'info' },
  PAUSED: { label: 'Paused', cls: 'pending' },
  DRAFT: { label: 'Draft', cls: 'offline' },
  ENDED: { label: 'Ended', cls: 'offline' },
};
export const stateOf = (m: Pick<Message, 'status' | 'startAt'>) => STATE[isScheduled(m as Message) ? 'SCHEDULED' : m.status];

/** "All guests", "2 floors · VIP", … */
export function audienceText(a: Audience): string {
  const parts: string[] = [];
  if (a.zoneIds?.length) parts.push(`${a.zoneIds.length} floor${a.zoneIds.length > 1 ? 's' : ''}`);
  if (a.roomIds?.length) parts.push(`${a.roomIds.length} room${a.roomIds.length > 1 ? 's' : ''}`);
  if (a.roomKinds?.length) parts.push(a.roomKinds.map((k) => k.replace('_', ' ').toLowerCase()).join(', '));
  if (a.packageIds?.length) parts.push(`${a.packageIds.length} package${a.packageIds.length > 1 ? 's' : ''}`);
  if (a.languages?.length) parts.push(a.languages.join(', '));
  if (a.vip !== undefined) parts.push(a.vip ? 'VIP' : 'Non-VIP');
  if (a.occupiedOnly) parts.push('occupied');
  return parts.length ? parts.join(' · ') : 'All guests';
}

/** Body for POST / PATCH /messages from a (partial) campaign. */
export function messageBody(m: Partial<Message>) {
  return {
    type: m.type,
    format: m.format,
    name: m.name || null,
    title: m.title || null,
    body: m.body || null,
    imageAssetId: m.imageAssetId ?? null,
    ctaLabel: m.ctaLabel || null,
    qrUrl: m.qrUrl || null,
    audience: m.audience ?? {},
    trigger: m.trigger,
    triggerHours: m.trigger === 'BEFORE_CHECKOUT' ? m.triggerHours ?? 12 : null,
    startAt: m.startAt ?? null,
    endAt: m.endAt ?? null,
    dailyFrom: m.dailyFrom || null,
    dailyTo: m.dailyTo || null,
    frequency: m.frequency,
    everyHours: m.frequency === 'EVERY_HOURS' ? m.everyHours ?? 24 : null,
    maxImpressions: m.maxImpressions ?? null,
    stopOnDismiss: m.stopOnDismiss ?? true,
    durationSec: m.durationSec ?? null,
    status: m.status,
  };
}

/** The campaign as a TV pop-up: photo, title, text and button. */
export function CampaignVisual({ m, img }: { m: Partial<Message>; img: string | null }) {
  return (
    <div className="campaign-visual" style={img ? { backgroundImage: `linear-gradient(90deg, rgba(10,8,30,.82) 30%, rgba(10,8,30,.05) 75%), url(${img})` } : undefined}>
      <strong className="cv-title">{m.title || m.name || 'Your title'}</strong>
      {m.body && <span className="cv-body">{m.body}</span>}
      {m.ctaLabel && <span className="cv-cta">{m.ctaLabel}</span>}
    </div>
  );
}

function StatCard({ icon, tone, value, label, sub }: { icon: ReactNode; tone: string; value: number; label: string; sub: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
        <span className="stat-tile-sub">{sub}</span>
      </span>
    </div>
  );
}

/** Marketing: campaigns (promotions, announcements), messages already sent, and their analytics. */
export function MarketingPage() {
  const site = useSite();
  const { can } = useSession();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'campaigns' | 'direct' | 'analytics'>('campaigns');
  const [sending, setSending] = useState(false);
  const campaigns = useQuery({
    queryKey: ['messages', site?.id, 'campaigns'],
    queryFn: () => api.get<{ items: Message[] }>(`/sites/${site!.id}/messages?view=campaigns`),
    enabled: !!site,
  });
  const direct = useQuery({
    queryKey: ['messages', site?.id, 'direct'],
    queryFn: () => api.get<{ items: Message[] }>(`/sites/${site!.id}/messages?view=direct`),
    enabled: !!site,
  });
  const c = campaigns.data?.items ?? [];
  return (
    <>
      <PageHeader
        title="Marketing & Messages"
        sub="Create and manage campaigns and guest messages shown on the TVs. Emergency messages always have priority, then front desk, then marketing."
        actions={
          site &&
          can('OPERATOR') && (
            <>
              <button className="btn" onClick={() => setSending(true)}>
                <MessageSquare size={16} /> Send message
              </button>
              {can('ADMIN') && (
                <button className="btn primary" onClick={() => navigate('/marketing/campaigns/new')}>
                  <Plus size={16} /> New campaign
                </button>
              )}
            </>
          )
        }
      />
      <NeedsSite>
        {site && (
          <>
            <div className="stat-strip">
              <StatCard icon={<Megaphone size={22} />} tone="violet" value={c.length} label="Total campaigns" sub="All time campaigns" />
              <StatCard icon={<Play size={22} />} tone="green" value={c.filter((m) => m.status === 'ACTIVE' && !isScheduled(m)).length} label="Active campaigns" sub="Currently showing on TVs" />
              <StatCard icon={<CalendarDays size={22} />} tone="violet" value={c.filter(isScheduled).length} label="Scheduled campaigns" sub="Starting later" />
              <StatCard icon={<Mail size={22} />} tone="blue" value={direct.data?.items.length ?? 0} label="Guest messages" sub="Direct messages to guests" />
            </div>
            <div className="line-tabs">
              <button className={tab === 'campaigns' ? 'on' : ''} onClick={() => setTab('campaigns')}>
                <Megaphone size={17} /> Campaigns
              </button>
              <button className={tab === 'direct' ? 'on' : ''} onClick={() => setTab('direct')}>
                <Mail size={17} /> Guest messages
              </button>
              <button className={tab === 'analytics' ? 'on' : ''} onClick={() => setTab('analytics')}>
                <BarChart3 size={17} /> Analytics
              </button>
            </div>
            {tab === 'analytics' ? (
              <div className="card">
                <Analytics siteId={site.id} />
              </div>
            ) : (
              <MessageList key={tab} siteId={site.id} view={tab} query={tab === 'campaigns' ? campaigns : direct} />
            )}
          </>
        )}
      </NeedsSite>
      {sending && site && <SendMessageModal siteId={site.id} onClose={() => setSending(false)} />}
    </>
  );
}

function MessageList({ siteId, view, query }: { siteId: string; view: 'campaigns' | 'direct'; query: { data?: { items: Message[] }; isLoading: boolean; error: unknown } }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const navigate = useNavigate();
  const url = useAssetUrls();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['messages', siteId] });
  const setStatus = useMutation({ mutationFn: (m: { id: string; status: string }) => api.patch(`/messages/${m.id}`, { status: m.status }), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/messages/${id}`), onSuccess: invalidate });
  const duplicate = useMutation({
    mutationFn: (m: Message) => api.post(`/sites/${siteId}/messages`, { ...messageBody(m), name: `${m.name || m.title || 'Campaign'} (copy)`, status: 'DRAFT' }),
    onSuccess: invalidate,
  });
  const [q, setQ] = useState('');
  const [type, setType] = useState('all');
  const [status, setStatusF] = useState('all');
  const [sort, setSort] = useState('new');
  const [selId, setSelId] = useState<string | null>(null);
  const all = query.data?.items ?? [];
  const types = [...new Set(all.map((m) => m.type))];
  const items = all
    .filter((m) => {
      const s = q.trim().toLowerCase();
      if (s && ![m.name, m.title, m.body].some((x) => x?.toLowerCase().includes(s))) return false;
      if (type !== 'all' && m.type !== type) return false;
      if (status !== 'all' && (status === 'SCHEDULED' ? !isScheduled(m) : m.status !== status || isScheduled(m))) return false;
      return true;
    })
    .sort((a, b) =>
      sort === 'old' ? a.createdAt.localeCompare(b.createdAt) : sort === 'name' ? (a.name || a.title || '').localeCompare(b.name || b.title || '') : b.createdAt.localeCompare(a.createdAt),
    );
  const { rows: paged, pager } = usePaged(items);
  const sel = selId ? all.find((m) => m.id === selId) ?? null : null;
  const admin = can('ADMIN');
  const edit = (m: Message) => navigate(`/marketing/campaigns/${m.id}`);
  const del = (m: Message) => ask(`Delete ${m.name || m.title || 'this message'}? It stops showing on the TVs.`, () => remove.mutate(m.id, { onSuccess: () => setSelId(null) }));

  if (query.isLoading) return <Loading />;
  return (
    <div className={`mk-layout${sel ? ' with-detail' : ''}`}>
      <div>
        <div className="filters" style={{ marginBottom: 12 }}>
          <label className="search">
            <Search size={16} />
            <input placeholder={view === 'campaigns' ? 'Search campaigns…' : 'Search messages…'} value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <Select value={type} onChange={(e) => setType(e.target.value)} aria-label="Type">
            <option value="all">All types</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </Select>
          <Select value={status} onChange={(e) => setStatusF(e.target.value)} aria-label="Status">
            <option value="all">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="SCHEDULED">Scheduled</option>
            <option value="PAUSED">Paused</option>
            <option value="DRAFT">Draft</option>
            <option value="ENDED">Ended</option>
          </Select>
          <Select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
            <option value="new">Created (newest)</option>
            <option value="old">Created (oldest)</option>
            <option value="name">Name</option>
          </Select>
        </div>
        {items.length === 0 ? (
          <div className="card">
            <Empty>
              {all.length
                ? 'Nothing matches.'
                : view === 'campaigns'
                  ? 'No campaigns yet. A campaign is a promotion or announcement with an audience (rooms, floors, VIP, language…) and a schedule.'
                  : 'No guest messages yet. Front-desk and PMS messages appear here.'}
            </Empty>
          </div>
        ) : (
          <div className="mk-list">
            <div className="mk-head">
              <span>{view === 'campaigns' ? 'Campaign' : 'Message'}</span>
              <span>Type</span>
              <span>Display</span>
              <span>Schedule / when</span>
              <span>Audience</span>
              <span>Status</span>
              <span>Created</span>
              <span />
            </div>
            {paged.map((m) => {
              const st = stateOf(m);
              const [fg, bg] = TYPE_TINT[m.type];
              const img = url(m.imageAssetId);
              return (
                <div key={m.id} className={`mk-row${sel?.id === m.id ? ' on' : ''}`} onClick={() => setSelId(m.id)}>
                  <span className="row" style={{ gap: 12, flexWrap: 'nowrap', minWidth: 0 }}>
                    <span className="fac-thumb mk">{img ? <img src={img} alt="" /> : <ImageOff size={18} />}</span>
                    <span style={{ minWidth: 0 }}>
                      <strong className="mk-name">{m.name || m.title || '(untitled)'}</strong>
                      <span className="muted fac-sub">{m.body}</span>
                    </span>
                  </span>
                  <span>
                    <span className="chip" style={{ color: fg, background: bg, whiteSpace: 'nowrap' }}>
                      {m.type === 'EMERGENCY' && <Siren size={11} />} {TYPE_LABEL[m.type]}
                    </span>
                  </span>
                  <span className="mk-cell">
                    <Monitor size={16} /> {FORMAT_LABEL[m.format]}
                  </span>
                  <span className="mk-cell" style={{ alignItems: 'flex-start' }}>
                    <Clock3 size={16} style={{ marginTop: 2 }} />
                    <span>
                      {TRIGGER_LABEL[m.trigger]}
                      <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                        {FREQ_LABEL[m.frequency].replace(' (every time conditions match)', '')}
                      </span>
                    </span>
                  </span>
                  <span className="mk-cell">
                    <Users size={16} /> {audienceText(m.audience)}
                  </span>
                  <span>
                    <span className={`state-pill ${st.cls}`} style={{ whiteSpace: 'nowrap' }}>
                      <span className="state-dot" /> {st.label}
                    </span>
                  </span>
                  <span className="muted" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                    {timeAgo(m.createdAt)}
                  </span>
                  <span onClick={(e) => e.stopPropagation()} style={{ textAlign: 'right' }}>
                    {(admin || (view === 'direct' && can('OPERATOR'))) && (
                      <Menu
                        minWidth={190}
                        button={({ onClick }) => (
                          <button type="button" className="row-btn" title="More" onClick={onClick}>
                            <MoreVertical size={16} />
                          </button>
                        )}
                      >
                        {admin && view === 'campaigns' && (
                          <>
                            <button type="button" className="more-item" onClick={() => edit(m)}>
                              <Pencil size={15} /> Edit
                            </button>
                            {m.status === 'ACTIVE' ? (
                              <button type="button" className="more-item" onClick={() => setStatus.mutate({ id: m.id, status: 'PAUSED' })}>
                                <Pause size={15} /> Pause
                              </button>
                            ) : (
                              <button type="button" className="more-item" onClick={() => setStatus.mutate({ id: m.id, status: 'ACTIVE' })}>
                                <Play size={15} /> {m.status === 'DRAFT' ? 'Publish' : 'Activate'}
                              </button>
                            )}
                            <button type="button" className="more-item" onClick={() => duplicate.mutate(m)}>
                              <Copy size={15} /> Duplicate
                            </button>
                          </>
                        )}
                        {view === 'direct' && m.status === 'ACTIVE' && can('OPERATOR') && (
                          <button type="button" className="more-item" onClick={() => setStatus.mutate({ id: m.id, status: 'ENDED' })}>
                            <X size={15} /> Withdraw
                          </button>
                        )}
                        {admin && (
                          <button type="button" className="more-item danger-item" onClick={() => del(m)}>
                            <Trash2 size={15} /> Delete
                          </button>
                        )}
                      </Menu>
                    )}
                  </span>
                </div>
              );
            })}
            <Pager {...pager} />
          </div>
        )}
        <ErrorBox error={query.error ?? setStatus.error ?? remove.error ?? duplicate.error} />
      </div>

      {sel && (
        <div className="card mk-detail">
          <div className="row" style={{ flexWrap: 'nowrap', gap: 8 }}>
            <Megaphone size={18} color="var(--brand)" />
            <strong style={{ fontSize: 16, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sel.name || sel.title || '(untitled)'}</strong>
            <span className={`state-pill ${stateOf(sel).cls}`}>
              <span className="state-dot" /> {stateOf(sel).label}
            </span>
            <button className="icon-btn" style={{ marginLeft: 'auto' }} onClick={() => setSelId(null)} title="Close">
              <X size={16} />
            </button>
          </div>
          <div style={{ marginTop: 12 }}>
            <CampaignVisual m={sel} img={url(sel.imageAssetId)} />
          </div>
          <h4 style={{ margin: '16px 0 4px' }}>{view === 'campaigns' ? 'Campaign details' : 'Message details'}</h4>
          <div className="fac-detail-list" style={{ borderTop: 0, marginTop: 0, paddingTop: 0 }}>
            <div className="fac-detail-row">
              <Tag size={16} />
              <span>Type</span>
              <span className="fac-detail-val">
                <span className="chip" style={{ color: TYPE_TINT[sel.type][0], background: TYPE_TINT[sel.type][1] }}>
                  {TYPE_LABEL[sel.type]}
                </span>
              </span>
            </div>
            <div className="fac-detail-row">
              <Monitor size={16} />
              <span>Display format</span>
              <span className="fac-detail-val">{FORMAT_LABEL[sel.format]}</span>
            </div>
            <div className="fac-detail-row" style={{ alignItems: 'flex-start' }}>
              <CalendarClock size={16} />
              <span>Schedule</span>
              <span className="fac-detail-val">
                {TRIGGER_LABEL[sel.trigger]}
                <span className="muted" style={{ display: 'block', fontWeight: 500, fontSize: 12 }}>
                  {FREQ_LABEL[sel.frequency]}
                  {sel.dailyFrom && sel.dailyTo ? ` · ${sel.dailyFrom}–${sel.dailyTo}` : ''}
                </span>
              </span>
            </div>
            <div className="fac-detail-row">
              <Users size={16} />
              <span>Audience</span>
              <span className="fac-detail-val">{audienceText(sel.audience)}</span>
            </div>
            <div className="fac-detail-row">
              <CircleDot size={16} />
              <span>Created</span>
              <span className="fac-detail-val">{timeAgo(sel.createdAt)}</span>
            </div>
          </div>
          {sel.body && (
            <>
              <h4 style={{ margin: '12px 0 4px' }}>Description</h4>
              <p className="muted" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>
                {sel.body}
              </p>
            </>
          )}
          {admin && (
            <div className="row" style={{ gap: 8, marginTop: 16, flexWrap: 'nowrap' }}>
              {view === 'campaigns' && (
                <>
                  <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={() => edit(sel)}>
                    <Pencil size={15} /> Edit
                  </button>
                  <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={() => duplicate.mutate(sel)}>
                    <Copy size={15} /> Duplicate
                  </button>
                </>
              )}
              <button className="btn danger-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => del(sel)}>
                <Trash2 size={15} /> Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


const statusBadge = (s: Message['status']) => <span className={`badge${s === 'ACTIVE' ? ' ok' : s === 'PAUSED' ? ' warn' : ''}`}>{s.toLowerCase()}</span>;

interface AnalyticsRow {
  id: string;
  type: Message['type'];
  format: Message['format'];
  name: string | null;
  title: string | null;
  status: Message['status'];
  createdAt: string;
  events: Record<string, { count: number; devices: number }>;
}

function Analytics({ siteId }: { siteId: string }) {
  const a = useQuery({
    queryKey: ['messages', siteId, 'analytics'],
    queryFn: () => api.get<{ items: AnalyticsRow[] }>(`/sites/${siteId}/messages/analytics`),
    refetchInterval: 60_000,
  });
  if (a.isLoading) return <Loading />;
  const items = a.data?.items ?? [];
  if (!items.length) return <Empty>No data yet. TVs report when a message is delivered, shown, closed or used.</Empty>;
  const n = (r: AnalyticsRow, k: string) => r.events[k]?.devices ?? 0;
  return (
    <table>
      <thead>
        <tr>
          <th>Message</th>
          <th>Type</th>
          <th>Delivered (TVs)</th>
          <th>Displayed (TVs)</th>
          <th>Closed</th>
          <th>Button / QR</th>
          <th>Engagement</th>
        </tr>
      </thead>
      <tbody>
        {items.map((r) => {
          const shown = n(r, 'DISPLAYED');
          const acted = n(r, 'CTA') + n(r, 'QR_SCANNED');
          return (
            <tr key={r.id}>
              <td>
                <strong>{r.name || r.title || '(untitled)'}</strong> {statusBadge(r.status)}
              </td>
              <td>{r.type === 'EMERGENCY' ? <span className="badge danger"><Siren size={11} /> emergency</span> : r.type.toLowerCase()}</td>
              <td>{n(r, 'DELIVERED')}</td>
              <td>{shown}</td>
              <td>{n(r, 'DISMISSED')}</td>
              <td>{acted}</td>
              <td>{shown ? `${Math.round((acted / shown) * 100)}%` : '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
